import { useEffect, useRef } from 'react';

function getHashTarget(): HTMLElement | null {
    const id = window.location.hash.slice(1);
    if (!id) return null;

    try {
        return document.getElementById(decodeURIComponent(id));
    } catch {
        return document.getElementById(id);
    }
}

/**
 * Scrolls to the element targeted by the URL hash once `isReady` is true.
 *
 * The native browser anchor scroll fails when the target is rendered client-side after a loading state
 * (e.g. a skeleton): the hash is set while the element does not exist yet. This hook keeps the hash
 * "pending" (on page load and on every `hashchange`) and scrolls to it as soon as the content is ready.
 */
export function useScrollToHash(isReady: boolean) {
    const isPendingRef = useRef(false);
    const isReadyRef = useRef(isReady);
    isReadyRef.current = isReady;

    useEffect(() => {
        isPendingRef.current = Boolean(window.location.hash);

        const onHashChange = () => {
            if (isReadyRef.current && getHashTarget()) {
                isPendingRef.current = false;
                return;
            }
            isPendingRef.current = true;
        };

        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    useEffect(() => {
        if (!isReady || !isPendingRef.current) return;

        const target = getHashTarget();
        if (!target) return;

        isPendingRef.current = false;
        target.scrollIntoView();
    }, [isReady]);
}
